// CRM-iframe bridge.
//
// When telegram-tt is embedded inside the CRM at https://tg.<apex>/?accountId=<id>,
// we don't want to ask the user to log in again — the CRM already holds a valid
// auth key for that account. This module:
//
//  1. Detects iframe mode by the presence of ?accountId in the URL.
//  2. Asks the parent SPA for the session payload via postMessage.
//  3. Persists the received {mainDcId, keys, isTest} into localStorage in
//     telegram-tt's "legacy single-account" format BEFORE init() runs, so
//     hasStoredSession() / loadStoredSession() pick it up on the very first
//     read.
//  4. After init the parent gets a `tg:ready` notification so it can hide its
//     loading overlay.
//
// Origin allow-list: only the parent window is trusted, and only when its
// origin matches the `crmOrigin` query param (which is set by the CRM SPA
// from window.location.origin when constructing the iframe URL). Because
// the iframe lives on a different subdomain than the SPA, localStorage and
// IndexedDB are naturally isolated per CRM account — no slot trick required.

import type { ApiSessionData } from '../api/types';

import HttpStream from '../lib/gramjs/extensions/HttpStream';
import PromisedWebSockets from '../lib/gramjs/extensions/PromisedWebSockets';
import { MAIN_IDB_STORE } from './browser/idb';
import { storeSession } from './sessions';

const ACCOUNT_ID_PARAM = 'accountId';
const CRM_ORIGIN_PARAM = 'crmOrigin';
const SESSION_TIMEOUT_MS = 15000;

// Device identity that the CRM picked for this account (server-side
// fingerprint preset → resolved deviceModel/systemVersion/appVersion/lang).
// Must override gramjs's navigator.userAgent default in iframe mode,
// otherwise initConnection ships the real browser UA and Telegram replaces
// the carefully chosen "iPhone 15"/"MacBook Pro" identity with whatever the
// browser reports — which is what users see as "Mozilla/5.0 (Macintosh;
// Intel Mac OS X 10_15_7)" in Settings → Devices after opening the iframe.
export interface CrmFingerprint {
  deviceModel?: string;
  systemVersion?: string;
  appVersion?: string;
  langCode?: string;
  langPack?: string;
}

let cachedQueryParams: URLSearchParams | undefined;
let cachedFingerprint: CrmFingerprint | undefined;
let cachedRelay: { dcDomain: string; authParams: string } | undefined;
let cachedApi: { apiId: number; apiHash: string } | undefined;

function getQuery(): URLSearchParams {
  if (!cachedQueryParams) {
    cachedQueryParams = new URLSearchParams(window.location.search);
  }
  return cachedQueryParams;
}

export function isCrmIframeMode(): boolean {
  return Boolean(getQuery().get(ACCOUNT_ID_PARAM));
}

export function getCrmAccountId(): string | undefined {
  return getQuery().get(ACCOUNT_ID_PARAM) || undefined;
}

export function getCrmOrigin(): string | undefined {
  return getQuery().get(CRM_ORIGIN_PARAM) || undefined;
}

export function getCrmFingerprint(): CrmFingerprint | undefined {
  return cachedFingerprint;
}

// Relay-routing parameters resolved by maybeInjectCrmSession() for the
// active account. Surfaced so the main-thread initApi caller can forward
// them to the gramjs worker via ApiInitialArgs.crmRelayDomain +
// crmAuthParams — the worker then re-applies the WSS prototype patch on
// its own module-instance.
//
// dcDomain: e.g. "dc.tgcrm.app". The patched getWebSocketLink builds
//           wss://zws<N>.<dcDomain>:443/apiws?<authParams>.
// authParams: e.g. "_auth=ws::acc::token", the single ephemeral query
//             string the relay validates against Redis (single-use, 60s
//             TTL). Mirrors crmchat.ai and matches web.telegram.org/a/'s
//             own zws<N>.web.telegram.org URL shape byte-for-byte except
//             for the host suffix.
export function getCrmRelay(): { dcDomain: string; authParams: string } | undefined {
  return cachedRelay;
}

// Telegram api_id / api_hash for the active account, as resolved by the
// CRM api at connection-data time. The api ships the same values the
// worker uses for this account so initConnection identity stays
// consistent across worker getMe probes and iframe MTProto sessions —
// mismatched api_id between the two is one of the freeze triggers we're
// trying to remove with T0. Forwarded into the gramjs worker via
// ApiInitialArgs.crmApiId / crmApiHash. If the payload doesn't carry
// them (older api / standalone build), worker falls back to the
// EnvironmentPlugin-baked process.env values.
export function getCrmApi(): { apiId: number; apiHash: string } | undefined {
  return cachedApi;
}

// WebRTC kill-switch lives at the browser permission layer now —
// Permissions-Policy header in apps/telegram-tt/nginx.conf disables
// camera, microphone, display-capture, geolocation; the parent SPA's
// iframe allow="clipboard-write" closes everything else by default.
// Chrome's `configurable: false` on window.RTCPeerConnection made JS
// override impossible without console-noise warnings, and Permissions-
// Policy is the canonical fix anyway.

// Server-visible beacon: fires a GET to the relay subdomain with a
// query string carrying the bridge stage + metadata. nginx-proxy
// access log captures every URL, so we can grep stage transitions
// server-side without needing the user's DevTools console (critical
// for mobile triage). Same-apex request stays inside our CSP
// (https://*.tgcrm.app + https://*.dc.tgcrm.app are allowed).
// Best-effort; never throws.
export function bridgeBeacon(stage: string, extra: Record<string, string | number | undefined> = {}) {
  try {
    const accountId = getCrmAccountId() ?? 'none';
    const qs = new URLSearchParams({
      _beacon: stage,
      acc: accountId,
      ts: String(Date.now()),
      ...Object.fromEntries(
        Object.entries(extra)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      ),
    });
    // Use sendBeacon when available (survives page unload), fallback
    // to fetch with keepalive. Path is arbitrary — we only care about
    // nginx logging the query.
    const url = `/__bridge?${qs.toString()}`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, '');
    } else {
      fetch(url, { method: 'GET', keepalive: true }).catch(() => {});
    }
  } catch {
    /* never block bridge on telemetry failure */
  }
}

// Tell the parent we're alive and ready for events. Safe to call repeatedly.
export function postReadyToCrm() {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage({ type: 'tg:ready' }, origin);
  } catch {
    // Cross-origin parent (or no parent) — best effort only.
  }
}

// Tell the parent the bridge handshake failed, with a human-readable
// reason. The SPA renders this in place of the iframe spinner so the
// operator gets an actionable error instead of an infinite "Подключение..."
// state. Safe to call before / instead of postReadyToCrm.
export function postBridgeFailureToCrm(reason: string) {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage({ type: 'tg:bridgeFailed', reason }, origin);
  } catch {
    /* best effort */
  }
}

// Re-route every wss connection through the CRM's relay subdomain pool.
// Built URL shape: `wss://zws<N>(-<sub>).<dcDomain>:443/apiws?<authParams>`,
// which byte-for-byte matches what web.telegram.org/a/ itself sends to
// zws<N>.web.telegram.org plus the ephemeral query token the relay
// validates via Redis. Without this patch telegram-tt would talk to
// Telegram's CDN directly, leaking the user's IP and bypassing the per-
// account SOCKS proxy assigned by the CRM.
//
// Idempotent — the original gramjs implementation is stashed on the
// prototype so calling this twice just re-binds to fresh params.
//
// Worker-safe: only touches the gramjs PromisedWebSockets prototype. Must
// be invoked inside the same module-realm where gramjs's WS will be
// instantiated — that's the worker thread, not main — so this is exported
// and called from api/gramjs/methods/client.ts during init().
export function applyProxyPatch(dcDomain: string, authParams: string) {
  const proto = PromisedWebSockets.prototype as any;
  const original: typeof proto.getWebSocketLink = proto.__originalGetWebSocketLink
    ?? proto.getWebSocketLink;
  proto.__originalGetWebSocketLink = original;

  proto.getWebSocketLink = function patched(
    ip: string,
    port: number,
    isTestServer?: boolean,
    isPremium?: boolean,
  ) {
    // gramjs passes the full Telegram-supplied hostname here, e.g.
    // `zws1.web.telegram.org` or `zws2-0.web.telegram.org`. We only
    // care about the leading `zws<N>(-<sub>)?` part — the rest is
    // `.web.telegram.org` we always replace with `.<our-dcDomain>`.
    // Match as a PREFIX (allow ".something" or empty after), don't
    // anchor with `$` — that's what broke iframe MTProto: the strict
    // anchor rejected the real hostname, the throw was swallowed by
    // MTProtoSender's retry loop, and start() hung forever.
    const dcMatch = /^zws(\d+)(-\d+)?(?:\.|$)/.exec(ip);
    if (!dcMatch) {
      const err = `[CRM bridge] refusing to dial non-zws WSS target "${ip}":${port} in iframe mode `
        + '(would bypass per-account SOCKS proxy and leak browser IP to Telegram)';
      // eslint-disable-next-line no-console
      console.error(err);
      bridgeBeacon('ws.getlink-rejected', { ip, port });
      throw new Error(err);
    }
    const dcId = dcMatch[1];
    const sub = dcMatch[2] ?? '';
    const suffix = `${isTestServer ? '_test' : ''}${isPremium ? '_premium' : ''}`;
    const url = `wss://zws${dcId}${sub}.${dcDomain}/apiws${suffix}?${authParams}`;
    // eslint-disable-next-line no-console
    console.info('[CRM bridge] WSS via relay', { ip, dcId, sub, url });
    bridgeBeacon('ws.getlink', { dcId, sub, urlLen: url.length });
    return url;
  };

  // Also wrap PromisedWebSockets.connect to confirm gramjs actually
  // invokes the dial and observe whether the new WebSocket fires
  // 'open'/'error'/'close' or hangs. Without this we can't tell
  // a CSP block from a stalled WS upgrade.
  if (!proto.__connectBeaconed) {
    proto.__connectBeaconed = true;
    const origConnect = proto.connect;
    proto.connect = async function tracedConnect(this: any, port: number, ip: string) {
      bridgeBeacon('ws.connect-call', { ip, port });
      try {
        const result = await origConnect.call(this, port, ip);
        bridgeBeacon('ws.connect-resolved', { ip });
        return result;
      } catch (err: any) {
        bridgeBeacon('ws.connect-threw', { ip, err: (err?.message ?? String(err)).slice(0, 80) });
        throw err;
      }
    };
  }
}

// Block the HTTP-transport fallback. MTProtoSender will fall back to
// HttpConnection after a few WSS failures (or eagerly when shouldForce is
// set). HttpStream goes through plain fetch() to https://zws<N>.web.telegram.org/apiw1,
// which bypasses the relay entirely — Telegram registers the request with
// the user's real IP and the account's "session location" silently changes.
// Returning an .invalid host makes the fetch fail fast, so the sender stays
// on the WSS path through the relay instead of leaking.
//
// We still keep shouldAllowHttpTransport=false in client.ts as primary
// defense; this is the belt-and-braces patch for any code path that
// constructs the URL directly (e.g. retry loops, sender exports).
//
// Worker-safe (no window/localStorage access). Invoked from the worker's
// client.ts during init() so it patches the same HttpStream module-instance
// gramjs actually uses.
export function applyHttpFallbackBlock() {
  const cls = HttpStream as unknown as {
    __originalGetURL?: (ip: string, port: number, isTestServer?: boolean, isPremium?: boolean) => string;
    getURL: (ip: string, port: number, isTestServer?: boolean, isPremium?: boolean) => string;
  };
  const original = cls.__originalGetURL ?? cls.getURL;
  cls.__originalGetURL = original;

  cls.getURL = function patched(ip, port, isTestServer, isPremium) {
    const dcMatch = /zws(\d)/.exec(ip);
    if (!dcMatch) return original(ip, port, isTestServer, isPremium);
    // eslint-disable-next-line no-console
    console.warn(
      `[CRM bridge] HTTP fallback to zws${dcMatch[1]} suppressed — would leak browser IP. `
      + 'Sender will retry over WSS through the relay.',
    );
    return 'https://crm-http-fallback-disabled.invalid/';
  };
}

// Single entry-point called from index.tsx before init().
// In iframe mode: fetch session from parent, persist to localStorage if it
// came through, and re-route gramjs's websockets through the CRM relay
// pool. In standalone mode: no-op. Always resolves (never throws) so init()
// can proceed even when the bridge fails — the user will just see the
// regular login screen instead of an auto-loaded chat list.
//
// Cross-origin isolation note: the iframe runs on a dedicated subdomain
// (https://tg.<apex>), so the browser already keeps every CRM account's
// localStorage / IndexedDB / SharedWorker namespace separate. No slot
// purge or `?account=<N>` workaround required — those existed only when
// the iframe shared the SPA origin.
export async function maybeInjectCrmSession(): Promise<void> {
  if (!isCrmIframeMode()) {
    // eslint-disable-next-line no-console
    console.info('[CRM bridge] standalone mode (no ?accountId), bridge disabled');
    return;
  }

  bridgeBeacon('bridge.start');

  // Wipe telegram-tt's serialized UI state. The previous account's
  // session blob (`account1` in localStorage) gets overwritten when
  // crmBridge.storeSession() fires with the new account's payload —
  // that part is safe. But the Redux-like UI cache (`tt-global-state`)
  // is shared across all CRM accounts on this origin AND lives in
  // IndexedDB (not localStorage — see global/cache.ts:64 →
  // MAIN_IDB_STORE.set). Without wiping it carries the previous
  // account's `currentUserId` + `users[]` + `chats[]` over, which is
  // what surfaces as the "Saved Messages" label (incoming peer's
  // tgId == cached self-id from the OLD account).
  //
  // Wipe both the IDB key and any legacy localStorage entries (older
  // builds + the brief slot-mode experiment yesterday left these).
  // After wipe telegram-tt rehydrates via updates.getDifference /
  // getDialogs on init — 1-3s, then UI is correct for the NEW
  // auth_key. Media-cache (`tt-media*`) lives in Cache API and stays
  // intact → avatars/files instant across switches.
  try {
    await MAIN_IDB_STORE.del('tt-global-state');
    await MAIN_IDB_STORE.del('tt-shared-state');
  } catch {
    // IDB might be disabled / blocked — proceed anyway; telegram-tt
    // just builds state from server on next read.
  }
  try {
    localStorage.removeItem('tt-global-state');
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('tt-global-state_')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignored
  }

  const accountId = getCrmAccountId();
  // eslint-disable-next-line no-console
  console.info('[CRM bridge] iframe mode — requesting session from parent', {
    accountId,
    crmOrigin: getCrmOrigin(),
    location: window.location.href,
  });

  try {
    const payload = await requestSessionPayloadFromCrm();
    if (!payload) {
      const reason = 'No sessionData received from parent SPA within timeout. '
        + 'Either the parent never attached its message listener, or '
        + 'the postMessage was rejected by origin checks.';
      // eslint-disable-next-line no-console
      console.error('[CRM bridge]', reason);
      bridgeBeacon('bridge.timeout');
      postBridgeFailureToCrm(reason);
      return;
    }
    bridgeBeacon('bridge.payload-received', {
      hasSession: payload.session ? 1 : 0,
      hasDcDomain: payload.dcDomain ? 1 : 0,
      hasAuthParams: payload.authParams ? 1 : 0,
      apiId: payload.apiId,
      hasApiHash: payload.apiHash ? 1 : 0,
    });

    // eslint-disable-next-line no-console
    console.info('[CRM bridge] received payload', {
      hasSession: !!payload.session,
      mainDcId: payload.session?.mainDcId,
      dcKeysAvailable: payload.session ? Object.keys(payload.session.keys || {}) : [],
      dcDomain: payload.dcDomain,
      authParamsLen: payload.authParams?.length,
      apiId: payload.apiId,
      apiHashLen: payload.apiHash?.length,
      fingerprint: payload.fingerprint,
    });

    // Hard requirement: the four fields gramjs needs to talk MTProto via
    // the relay. If any of them is missing the iframe can't safely
    // connect (would fall through to wss://web.telegram.org and either
    // be CSP-blocked or leak the user IP). Surface the precise gap so
    // the operator knows what to fix.
    const missing: string[] = [];
    if (!payload.session) missing.push('session');
    if (!payload.dcDomain) missing.push('dcDomain');
    if (!payload.authParams) missing.push('authParams');
    if (!payload.apiId || payload.apiId <= 0) missing.push('apiId');
    if (!payload.apiHash) missing.push('apiHash');
    if (missing.length > 0) {
      const reason = `Connection-data payload incomplete; missing fields: ${missing.join(', ')}. `
        + 'Server-side getConnectionData() probably rejected the account (no SOCKS proxy, '
        + 'inactive status) or the api response shape diverged.';
      // eslint-disable-next-line no-console
      console.error('[CRM bridge]', reason);
      bridgeBeacon('bridge.incomplete', { missing: missing.join(',') });
      postBridgeFailureToCrm(reason);
      return;
    }

    // storeSession writes both slot data and (when no ACCOUNT_SLOT) the
    // legacy single-account keys used by gramjs on init. From this point
    // on, hasStoredSession() in src/util/sessions.ts returns true and the
    // normal init flow loads the auth keys into a CallbackSession.
    storeSession(payload.session!);
    // eslint-disable-next-line no-console
    console.info('[CRM bridge] session stored to localStorage', {
      accountId,
      mainDcId: payload.session!.mainDcId,
    });

    // Cache for the main-thread initApi caller to forward into the worker
    // via ApiInitialArgs.crmRelayDomain + crmAuthParams. The actual
    // prototype patches must run inside the worker (where gramjs's WS
    // will instantiate) and are applied by applyProxyPatch /
    // applyHttpFallbackBlock from client.ts.
    cachedRelay = { dcDomain: payload.dcDomain!, authParams: payload.authParams! };
    cachedApi = { apiId: payload.apiId!, apiHash: payload.apiHash! };
    if (payload.fingerprint) cachedFingerprint = payload.fingerprint;
    // eslint-disable-next-line no-console
    console.info('[CRM bridge] caches populated', {
      dcDomain: cachedRelay.dcDomain,
      authParamsLen: cachedRelay.authParams.length,
      apiId: cachedApi.apiId,
      apiHashLen: cachedApi.apiHash.length,
      fingerprint: cachedFingerprint,
    });
    bridgeBeacon('bridge.cache-populated', {
      dcDomain: cachedRelay.dcDomain,
      apiId: cachedApi.apiId,
    });

    // Direct WebSocket smoke: open one against the relay with the same
    // URL gramjs will use, BEFORE gramjs gets a chance to dial. This
    // separates "WS itself doesn't work in this browser" (CSP/network
    // block) from "gramjs has an internal hang". Beacons report the
    // exact event the browser fires.
    try {
      const smokeUrl = `wss://zws1.${cachedRelay.dcDomain}/apiws?${cachedRelay.authParams}`;
      bridgeBeacon('smoke.opening', { urlLen: smokeUrl.length });
      const smokeWs = new WebSocket(smokeUrl, 'binary');
      const smokeTimer = setTimeout(() => {
        bridgeBeacon('smoke.timeout-5s', { readyState: smokeWs.readyState });
        try { smokeWs.close(); } catch { /* ignore */ }
      }, 5000);
      smokeWs.onopen = () => {
        bridgeBeacon('smoke.open');
        clearTimeout(smokeTimer);
        // Close immediately — we only wanted to know if it can open.
        try { smokeWs.close(1000, 'smoke-done'); } catch { /* ignore */ }
      };
      smokeWs.onerror = () => {
        bridgeBeacon('smoke.error', { readyState: smokeWs.readyState });
        clearTimeout(smokeTimer);
      };
      smokeWs.onclose = (e) => {
        bridgeBeacon('smoke.close', { code: e.code, reason: (e.reason || '').slice(0, 40), wasClean: e.wasClean ? 1 : 0 });
        clearTimeout(smokeTimer);
      };
    } catch (e: any) {
      bridgeBeacon('smoke.throw', { err: (e?.message ?? String(e)).slice(0, 80) });
    }
  } catch (err) {
    const reason = `Session injection threw: ${(err as Error)?.message ?? String(err)}`;
    // eslint-disable-next-line no-console
    console.error('[CRM bridge]', reason);
    bridgeBeacon('bridge.threw', { err: (err as Error)?.message?.slice(0, 80) ?? 'unknown' });
    postBridgeFailureToCrm(reason);
  }
}

interface CrmSessionPayload {
  session?: ApiSessionData;
  dcDomain?: string;
  authParams?: string;
  apiId?: number;
  apiHash?: string;
  fingerprint?: CrmFingerprint;
}

async function requestSessionPayloadFromCrm(): Promise<CrmSessionPayload | undefined> {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return undefined;

  return new Promise((resolve) => {
    let settled = false;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return;
      if (event.source !== window.parent) return;
      const data: any = event.data;
      if (!data || data.type !== 'crm:sessionData') return;
      const payload = data.payload;
      if (!payload || typeof payload !== 'object') return;

      const result: CrmSessionPayload = {};
      const session = payload.session;
      if (session && typeof session === 'object'
          && session.mainDcId && session.keys && typeof session.keys === 'object') {
        result.session = {
          mainDcId: session.mainDcId,
          keys: session.keys,
          isTest: Boolean(session.isTest),
        };
      }
      if (typeof payload.dcDomain === 'string' && payload.dcDomain.length > 0) {
        result.dcDomain = payload.dcDomain;
      }
      if (typeof payload.authParams === 'string' && payload.authParams.length > 0) {
        result.authParams = payload.authParams;
      }
      if (typeof payload.apiId === 'number' && payload.apiId > 0) {
        result.apiId = payload.apiId;
      }
      if (typeof payload.apiHash === 'string' && payload.apiHash.length > 0) {
        result.apiHash = payload.apiHash;
      }
      if (payload.fingerprint && typeof payload.fingerprint === 'object') {
        const fp = payload.fingerprint;
        result.fingerprint = {
          deviceModel: typeof fp.deviceModel === 'string' ? fp.deviceModel : undefined,
          systemVersion: typeof fp.systemVersion === 'string' ? fp.systemVersion : undefined,
          appVersion: typeof fp.appVersion === 'string' ? fp.appVersion : undefined,
          langCode: typeof fp.langCode === 'string' ? fp.langCode : undefined,
          langPack: typeof fp.langPack === 'string' ? fp.langPack : undefined,
        };
      }

      settled = true;
      window.removeEventListener('message', onMessage);
      resolve(result);
    };

    window.addEventListener('message', onMessage);

    try {
      window.parent.postMessage(
        { type: 'tg:requestSession', accountId: getCrmAccountId() },
        origin,
      );
    } catch {
      settled = true;
      window.removeEventListener('message', onMessage);
      resolve(undefined);
      return;
    }

    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', onMessage);
      // eslint-disable-next-line no-console
      console.warn('[CRM bridge] No sessionData from parent within timeout');
      resolve(undefined);
    }, SESSION_TIMEOUT_MS);
  });
}
